/**
 * Primitive Career operations (plan §6 M2-01): pure and immutable (the input is never changed; untouched
 * branches are shared). Every wave-2 command builds on these. They do not refuse anything: commands check
 * funds, space and services first and return a typed `CmdResult`; the few guards here catch bugs. The exception is
 * the goods trade pair `addGoods`/`removeGoods` (economy plan SY-2c): pure commands that check space, funds and
 * holdings themselves and return a `CmdResult`.
 */
import { resolveModule } from '../data/m2'
import { SIM_FACTION_IDS, WARE_IDS, type M2Data, type ModuleId, type SimFactionId, type WareId } from '../data/m2Schemas'
import rushSparesJson from '../data/rush-spares.json'
import strategicWaresJson from '../data/strategic/wares.json'
import { goodHoldT, hasHoldMult, holdUsedT } from './hullFit'
import { GOOD_IDS, goodsT, goodT, isRelayPartId, type GoodId } from './parts'
import { cargoFull, fail, formatTonnes, insufficientFunds, invalidAmount, ok, type CmdResult } from './result'
import type { CargoComponent, CargoContents, Career, LeaseEvent } from './types'

/** Below this magnitude a result counts as zero (float round-off in money and tonnes). */
export const EPSILON = 1e-6

/** A zero tonnage for every ware. */
export function emptyWares(): Record<WareId, number> {
  const out = {} as Record<WareId, number>
  for (const w of WARE_IDS) out[w] = 0
  return out
}

/** An empty pod content. */
export function emptyContents(): CargoContents {
  return { wares: emptyWares(), components: [] }
}

/** Does a pod hold anything (wares, goods or components)? SY-2b: a loaded pod is unpacked, never sold as a pod. */
export function contentsLoaded(contents: Readonly<CargoContents>): boolean {
  return waresT(contents.wares) > EPSILON || goodsT(contents.goods) > EPSILON || contents.components.length > 0
}

/** Tonnes of wares in the hold. */
export function waresT(wares: Readonly<Record<WareId, number>>): number {
  let t = 0
  for (const w of WARE_IDS) t += wares[w]
  return t
}

/**
 * Tonnes in the hold: every ware, every component's carried mass (a pod's mass includes its contents) and the
 * goods block (SY-2a; the relay parts are goods). M10 P5a6a: weighed by the flown hull's `holdMult` per cargo class.
 */
export function cargoUsedT(career: Career): number {
  return cargoUsedOnT(career, career.ship.defId)
}

/**
 * Tonnes the career's hold would take on ship def `defId` (M10 P5a6b H1: a ship switch weighs the cargo by the
 * target hull). A hull with a `fit.holdMult` counts each tonne by its cargo class (`career/hullFit.ts holdUsedT`);
 * every shipped hull has none, so this keeps today's sum.
 */
export function cargoUsedOnT(career: Career, defId: string): number {
  if (hasHoldMult(defId)) return holdUsedT(career.cargo, defId)
  let t = waresT(career.cargo.wares) + goodsT(career.cargo.parts)
  for (const c of career.cargo.components) t += c.massT
  return t
}

/** Free tonnes (never negative). */
export function cargoFreeT(career: Career): number {
  const free = career.cargo.capacityT - cargoUsedT(career)
  return free > 0 ? free : 0
}

/**
 * Adds `delta` credits (negative to spend). Commands check funds first: a result below zero is a bug and
 * throws a RangeError (round-off within `EPSILON` settles at 0).
 */
export function withWallet(career: Career, delta: number): Career {
  if (!Number.isFinite(delta)) throw new RangeError(`withWallet: delta ${delta} is not finite`)
  let wallet = career.wallet + delta
  if (wallet < 0) {
    if (wallet < -EPSILON) throw new RangeError(`withWallet: wallet would go negative (${career.wallet} + ${delta})`)
    wallet = 0
  }
  return { ...career, wallet }
}

/**
 * Adds `deltaT` tonnes of `ware` (negative to remove), never below 0. Capacity is the caller's check.
 *
 * M10 P5a3c (the P5a12d precondition): a removal (sale, jettison, transfer) lowers the ware's processed tonnes
 * (`CargoState.processed`) in proportion and clamps them to the tonnes left, so a hold never holds more processed
 * than it has (`schema.ts checkProcessed`). Nothing changes when the ware has no processed entry (every hold today).
 */
export function withWare(career: Career, ware: WareId, deltaT: number): Career {
  if (!Number.isFinite(deltaT)) throw new RangeError(`withWare: delta ${deltaT} is not finite`)
  const before = career.cargo.wares[ware]
  let t = before + deltaT
  if (t < EPSILON) t = 0
  const wares = { ...career.cargo.wares, [ware]: t }
  const processed = career.cargo.processed
  const p = processed?.[ware]
  if (processed === undefined || p === undefined || deltaT >= 0) return { ...career, cargo: { ...career.cargo, wares } }
  const scaled = before > 0 ? (p * t) / before : 0
  return { ...career, cargo: { ...career.cargo, wares, processed: { ...processed, [ware]: scaled < t ? scaled : t } } }
}

/**
 * Adds `deltaT` units of good `id` (negative to remove; floats allowed), never below 0 (round-off within
 * `EPSILON` settles at 0); creates the goods block (`cargo.parts`) on first use. Capacity is the caller's check.
 */
export function withGood(career: Career, id: GoodId, deltaT: number): Career {
  if (!Number.isFinite(deltaT)) throw new RangeError(`withGood: delta ${deltaT} is not finite`)
  const goods = career.cargo.parts ?? {}
  let t = (goods[id] ?? 0) + deltaT
  if (t < EPSILON) t = 0
  return { ...career, cargo: { ...career.cargo, parts: { ...goods, [id]: t } } }
}

// ---- Goods trade (economy plan SY-2c): pure commands, not cargo.ts (M7 S14 owns it) -------------

/** The player-facing name of each good (the strategic ware's name; Rush spares from `rush-spares.json name`, MC-h4a). */
export const GOOD_NAMES: Readonly<Record<GoodId, string>> = (() => {
  const out = {} as Record<GoodId, string>
  for (const g of GOOD_IDS) out[g] = strategicWaresJson.wares.find((w) => w.id === g)?.name ?? g
  out.rush_spares = rushSparesJson.name
  return Object.freeze(out)
})()

/**
 * Whether goods trading allows `side` of good `id` (the goods deploy rule, R3-H2): with `career.json
 * goods.tradeEnabled` off, only buying one of the three relay parts is allowed (SY-2e's Parts tab buys through it);
 * every sale and every other buy is refused. With it on, everything is allowed.
 */
export function goodTradeAllowed(tradeEnabled: boolean, id: GoodId, side: 'buy' | 'sell'): boolean {
  return tradeEnabled || (side === 'buy' && isRelayPartId(id))
}

function validQty(qty: number): boolean {
  return Number.isFinite(qty) && qty > 0
}

function validCredits(credits: number): boolean {
  return Number.isFinite(credits) && credits >= 0
}

/**
 * Buys `qty` units of good `id` for `credits` (≥ 0): refuses a bad amount, a hold without room for `qty × massT`
 * tonnes, or a wallet short of `credits`. Pure; the price comes from the caller (the strategic quote).
 */
export function addGoods(career: Career, id: GoodId, qty: number, credits: number): CmdResult<Career> {
  if (!validQty(qty)) return invalidAmount('quantity', qty)
  if (!validCredits(credits)) return invalidAmount('price', credits)
  const needT = goodHoldT(career.ship.defId, id, qty) // M10 P5a6b H2: class-weighted (qty × mass on every shipped hull)
  const free = cargoFreeT(career)
  if (needT > free + EPSILON) return cargoFull(needT, free)
  if (credits > career.wallet) return insufficientFunds(credits, career.wallet)
  return ok(withWallet(withGood(career, id, qty), -credits))
}

/**
 * Sells `qty` units of good `id` for `credits` (≥ 0, counted as earned): refuses a bad amount or more than the hold
 * carries (within `EPSILON`, which takes exactly what is held). Pure.
 */
export function removeGoods(career: Career, id: GoodId, qty: number, credits: number): CmdResult<Career> {
  if (!validQty(qty)) return invalidAmount('quantity', qty)
  if (!validCredits(credits)) return invalidAmount('price', credits)
  const held = goodT(career.cargo.parts, id)
  if (qty > held + EPSILON) {
    return fail('notInCargo', `Only ${formatTonnes(held)} t of ${GOOD_NAMES[id]} in the hold.`, { good: id, held, requested: qty })
  }
  const next = withWallet(withGood(career, id, -(qty < held ? qty : held)), credits)
  return ok({ ...next, stats: { ...next.stats, earned: next.stats.earned + credits } })
}

/** Adds a component to the hold. Capacity is the caller's check. */
export function withComponent(career: Career, component: CargoComponent): Career {
  return { ...career, cargo: { ...career.cargo, components: [...career.cargo.components, component] } }
}

/** Removes the component with `id` (the same career when it is not in the hold). */
export function withoutComponent(career: Career, id: string): Career {
  const components = career.cargo.components.filter((c) => c.id !== id)
  if (components.length === career.cargo.components.length) return career
  return { ...career, cargo: { ...career.cargo, components } }
}

/** Clamps a standing to −100..100. */
export function clampStanding(value: number): number {
  return value < -100 ? -100 : value > 100 ? 100 : value
}

/** Adds `delta` standing with `faction`, clamped to −100..100. */
export function withStanding(career: Career, faction: SimFactionId, delta: number): Career {
  if (!SIM_FACTION_IDS.includes(faction)) throw new RangeError(`withStanding: unknown faction "${String(faction)}"`)
  return { ...career, standings: { ...career.standings, [faction]: clampStanding(career.standings[faction] + delta) } }
}

/** Folds the runtime game clock into the career. */
export function withGameTime(career: Career, t: number): Career {
  if (!(t >= 0) || !Number.isFinite(t)) throw new RangeError(`withGameTime: ${t} is not a valid game time`)
  return { ...career, gameTime: t }
}

/** Appends a lease-history row, keeping the last `limit` rows (`lease.json historyLimit`). */
export function withLeaseEvent(career: Career, event: LeaseEvent, limit: number): Career {
  const history = [...career.lease.history, event]
  if (history.length > limit) history.splice(0, history.length - limit)
  return { ...career, lease: { ...career.lease, history } }
}

/** A module's value (its price): fitted value, rebuild fees and sale prices build on it. */
export function moduleValue(data: Pick<M2Data, 'modules'>, id: ModuleId): number {
  return resolveModule(data, id).price
}
